import { SERVICES } from "@/lib/site";

const serviceBySlug = (slug) => SERVICES.find(s => s.slug === slug);

/**
 * City-specific landing pages for local SEO.
 * Each page targets a primary service × city query (e.g., "Custom Signs Toronto").
 * Unique intro, neighborhoods, and local hooks make these non-duplicate to Google.
 */
export const CITY_PAGES = [
  {
    slug: "signs-toronto",
    serviceSlug: "signs",
    city: "Toronto",
    title: "Custom Signs in Toronto",
    metaTitle: "Custom Signs Toronto · Storefront, Channel Letter & Reception Signs",
    metaDescription: "Custom signs in Toronto — channel letter, storefront, ACM, acrylic, reception, and dimensional signage. 3M Certified install. Free quotes for downtown Toronto, Yorkville, King West, Queen West & the GTA.",
    intro: "From King West storefronts to Yorkville boutiques and Liberty Village offices, Toronto businesses trust us for custom signage that looks designed — not stuck-on. We fabricate and install channel letters, dimensional ACM signs, illuminated lobby signage, wayfinding, and reception walls across downtown Toronto, North York, Etobicoke, and the Beaches. Permits, mounting, and finish are handled in-house.",
    neighborhoods: ["Downtown", "King West", "Queen West", "Yorkville", "Liberty Village", "The Beaches", "Yonge-Dundas", "Distillery District", "Leslieville", "North York", "Etobicoke", "Scarborough"],
    whyHere: [
      { t: "Permit-savvy", d: "We handle Toronto sign permits and condo-board approvals for commercial buildings." },
      { t: "Storefront experts", d: "Channel letters, light boxes, and ACM signs sized for King St, Queen St, and Yonge corridor frontages." },
      { t: "Same-week site visits", d: "Downtown site measurements within days — Toronto is our home base." },
    ],
  },
  {
    slug: "architectural-wraps-toronto",
    serviceSlug: "architectural-wraps",
    city: "Toronto",
    title: "Architectural Wraps in Toronto",
    metaTitle: "Architectural Wraps Toronto · Cabinet, Wall & Elevator Wraps",
    metaDescription: "Transform Toronto offices, retail, and condos with premium 3M and Avery architectural vinyl wraps. Cabinet, wall, elevator, and surface wraps installed across downtown Toronto and the GTA.",
    intro: "Toronto's commercial spaces — Bay Street boardrooms, Liberty Village creative offices, condo amenity floors — increasingly skip renovation costs and choose architectural wraps instead. We resurface cabinets, elevator interiors, reception walls, columns, and millwork using premium 3M Di-Noc and Avery architectural films. Installations are dust-free, fume-free, and usually complete in 1–3 days.",
    neighborhoods: ["Financial District", "King West", "Liberty Village", "Yorkville", "Entertainment District", "Distillery", "Leslieville", "North York", "Etobicoke", "Scarborough"],
    whyHere: [
      { t: "No renovation downtime", d: "Wrap over existing surfaces — no demolition, no contractors, no permits." },
      { t: "Condo & strata friendly", d: "Quiet, low-impact installs that pass property-management approval." },
      { t: "Premium finishes", d: "Wood grain, concrete, marble, metallic, and custom-printed textures." },
    ],
  },
  {
    slug: "signs-mississauga",
    serviceSlug: "signs",
    city: "Mississauga",
    title: "Custom Signs in Mississauga",
    metaTitle: "Custom Signs Mississauga · Storefront, Office & Channel Letter Signage",
    metaDescription: "Custom signs in Mississauga — Square One, City Centre, Erin Mills, Streetsville, and Port Credit. Channel letters, storefront, reception, and wayfinding signs with professional installation.",
    intro: "Mississauga businesses from Square One retailers to Airport Corporate Centre offices and Streetsville restaurants choose us for signage that reflects a polished, professional brand. We design, fabricate, and install channel letters, ACM signs, acrylic logo signs, and wayfinding systems across Mississauga's commercial corridors — including Hurontario, Dundas, and Mavis.",
    neighborhoods: ["Square One", "City Centre", "Streetsville", "Port Credit", "Erin Mills", "Meadowvale", "Cooksville", "Clarkson", "Lakeview", "Airport Corporate Centre"],
    whyHere: [
      { t: "Plaza & strip-mall approved", d: "Designs that meet Mississauga landlord and plaza sign-criteria packages." },
      { t: "Quick GTA-wide service", d: "Site visits to Mississauga within 48 hours — we serve Peel daily." },
      { t: "Office-park ready", d: "Indoor lobby signs and exterior monument signs for corporate parks along the 401 and 403." },
    ],
  },
  {
    slug: "architectural-wraps-mississauga",
    serviceSlug: "architectural-wraps",
    city: "Mississauga",
    title: "Architectural Wraps in Mississauga",
    metaTitle: "Architectural Wraps Mississauga · Cabinet, Wall & Office Refinishing",
    metaDescription: "Premium architectural vinyl wraps in Mississauga — cabinet, wall, elevator, and retail interior refinishing. Faster and cheaper than renovation. Free quotes across Peel Region.",
    intro: "From Square One retailers refreshing storefronts to office parks along Hurontario refinishing reception walls, Mississauga businesses choose architectural wraps for one reason: 80% of the impact of a renovation at 20% of the cost. We wrap cabinets, walls, doors, elevators, and entire retail interiors with 3M and Avery films designed for high-traffic commercial use.",
    neighborhoods: ["Square One", "Erin Mills", "Heartland", "Meadowvale", "Streetsville", "Port Credit", "Airport Corporate Centre", "Mavis & Britannia"],
    whyHere: [
      { t: "Retail-tested", d: "Surfaces hold up to high foot traffic in Square One, Heartland, and Erin Mills." },
      { t: "After-hours installs", d: "Evening and weekend appointments to avoid customer disruption." },
      { t: "10+ year material warranty", d: "Premium films built to last in commercial environments." },
    ],
  },
  {
    slug: "signs-vaughan",
    serviceSlug: "signs",
    city: "Vaughan",
    title: "Custom Signs in Vaughan",
    metaTitle: "Custom Signs Vaughan · VMC, Woodbridge & Concord Signage",
    metaDescription: "Custom signs in Vaughan — Vaughan Metropolitan Centre, Woodbridge, Concord, Maple, and Kleinburg. Channel letters, storefront, reception, and dimensional signage with professional install.",
    intro: "Vaughan's commercial growth — VMC towers, Vaughan Mills retailers, and the Highway 7 corridor — means more businesses than ever need standout signage. We work with Vaughan restaurants, contractors, dental clinics, and corporate offices to design, fabricate, and install channel letters, ACM signs, and indoor lobby signage that pulls weight in busy plazas and office complexes.",
    neighborhoods: ["Vaughan Metropolitan Centre", "Woodbridge", "Concord", "Maple", "Kleinburg", "Thornhill (Vaughan)", "Vellore", "Vaughan Mills"],
    whyHere: [
      { t: "Plaza & landlord compliant", d: "Designs aligned to Vaughan plaza criteria from day one — no costly revisions." },
      { t: "Industrial signage", d: "Large-format exterior signs for warehouses and contractor yards in Concord and Pine Valley." },
      { t: "Direct VMC service", d: "Tower lobby signs and wayfinding for Vaughan Metropolitan Centre developments." },
    ],
  },
  {
    slug: "window-film-toronto",
    serviceSlug: "window-graphics",
    city: "Toronto",
    title: "Frosted Window Film in Toronto",
    metaTitle: "Frosted Window Film Toronto · Privacy, Branding & Decorative Vinyl",
    metaDescription: "Frosted window film installation in Toronto — privacy film for offices, frosted branding for storefronts, one-way vision for retail. Fast professional install across downtown Toronto and the GTA.",
    intro: "Toronto offices and storefronts use frosted window film for three reasons: privacy without losing daylight, branded glass that doubles as marketing, and instant transformation of glass walls without renovation. We install frosted films, gradient privacy patterns, branded logo etches, and one-way vision graphics across downtown Toronto offices, retail storefronts on Queen and King, and condo amenity glass.",
    neighborhoods: ["Financial District", "King West", "Queen West", "Liberty Village", "Yorkville", "Entertainment District", "North York", "Etobicoke", "The Annex"],
    whyHere: [
      { t: "Same-day quotes", d: "We measure and quote downtown Toronto offices fast — most jobs scheduled within a week." },
      { t: "Premium materials", d: "3M and Avery films — bubble-free install with 7+ year material life." },
      { t: "Branded film design", d: "We design custom logo etches and gradient privacy patterns to match your brand." },
    ],
  },
  {
    slug: "window-film-mississauga",
    serviceSlug: "window-graphics",
    city: "Mississauga",
    title: "Frosted Window Film in Mississauga",
    metaTitle: "Frosted Window Film Mississauga · Office Privacy & Storefront Graphics",
    metaDescription: "Frosted window film for Mississauga offices and storefronts — privacy patterns, branded etches, and one-way vision installed professionally across Square One, Heartland, and the 401/403 office corridors.",
    intro: "Mississauga's corporate offices along the 401 and 403, Square One retailers, and medical clinics across Erin Mills all use frosted window film to add privacy, brand identity, and polished interiors — without permanent changes to the glass. We install frosted patterns, branded etches, and one-way vision graphics with bubble-free precision.",
    neighborhoods: ["Airport Corporate Centre", "Heartland", "Square One", "Erin Mills", "Streetsville", "Port Credit", "Meadowvale"],
    whyHere: [
      { t: "Office-park specialists", d: "We work with property managers in Airport Corporate Centre, Heartland, and Meadowvale weekly." },
      { t: "Clinic-ready privacy", d: "HIPAA-style privacy films designed for medical and dental offices across Mississauga." },
      { t: "After-hours install", d: "Evening installs available to avoid disrupting your business day." },
    ],
  },
  {
    slug: "signs-brampton",
    serviceSlug: "signs",
    city: "Brampton",
    title: "Custom Signs in Brampton",
    metaTitle: "Custom Signs Brampton · Storefront, Industrial & Office Signage",
    metaDescription: "Custom signs in Brampton — channel letter signs, storefront branding, warehouse signage, and indoor reception signs. Free quotes across Brampton and Peel Region.",
    intro: "Brampton's mix of retail plazas, industrial parks, and growing commercial corridors means signage that has to perform across very different environments — and we deliver. We fabricate channel letter exteriors, ACM panel signs, dimensional reception logos, and large-format industrial signs across Brampton's plazas, warehouses, and offices.",
    neighborhoods: ["Downtown Brampton", "Bramalea", "Mount Pleasant", "Heart Lake", "Springdale", "Castlemore", "Steeles Industrial", "Goreway Corridor"],
    whyHere: [
      { t: "Industrial & warehouse", d: "Large-format exterior signs and dock-door numbering for Brampton's industrial parks." },
      { t: "Plaza-aligned design", d: "Designs that meet Brampton plaza sign criteria from day one." },
      { t: "Quick turnaround", d: "Most channel letter projects ready for install within 2–3 weeks." },
    ],
  },
  {
    slug: "signs-markham",
    serviceSlug: "signs",
    city: "Markham",
    title: "Custom Signs in Markham",
    metaTitle: "Custom Signs Markham · Unionville, Cornell & Markham Centre Signage",
    metaDescription: "Custom signs in Markham — Unionville, Cornell, Markham Centre, and Thornhill. Channel letters, reception, dimensional, and wayfinding signage with professional install.",
    intro: "Markham's tech corridors, professional plazas, and Unionville heritage businesses all need signage that looks intentional, not generic. We fabricate and install channel letter signs, dimensional logo walls, lobby signs, and acrylic dimensional signage for offices, clinics, and retail across Markham Centre, Unionville, Cornell, and Thornhill.",
    neighborhoods: ["Markham Centre", "Unionville", "Cornell", "Thornhill (Markham)", "Cathedraltown", "Berczy", "Wismer", "Box Grove"],
    whyHere: [
      { t: "Tech-corridor ready", d: "Modern, brand-aligned signage for offices along the 404 and 407 tech corridors." },
      { t: "Heritage-friendly", d: "Designs that respect Unionville's heritage district aesthetic — wood, blade signs, gold leaf finishes." },
      { t: "Same-week site visits", d: "We cover Markham daily; site measurements within days." },
    ],
  },
  {
    slug: "architectural-wraps-scarborough",
    serviceSlug: "architectural-wraps",
    city: "Scarborough",
    title: "Architectural Wraps in Scarborough",
    metaTitle: "Architectural Wraps Scarborough · Cabinet, Wall & Retail Refinishing",
    metaDescription: "Premium architectural wraps in Scarborough — cabinet, wall, elevator, and retail interior refinishing. Transform your space faster and cheaper than renovation. Free quotes across Scarborough.",
    intro: "Scarborough retailers, dental clinics, restaurants, and offices use architectural wraps to refresh interiors without closing for renovation. We resurface cabinets, walls, elevators, and full retail interiors using premium 3M and Avery films — most installs completed in 1–3 days with zero demolition.",
    neighborhoods: ["Scarborough Town Centre", "Agincourt", "Malvern", "Birch Cliff", "Cliffside", "Wexford", "Bendale", "Dorset Park", "West Hill"],
    whyHere: [
      { t: "Retail-grade durability", d: "Films built for high-traffic Scarborough Town Centre and plaza retail environments." },
      { t: "No demolition", d: "Wrap over laminate, melamine, drywall, and metal — no contractors, no permits." },
      { t: "Weekend installs", d: "Evening and weekend appointments for restaurants and clinics that can't close." },
    ],
  },
];

export function getCityPage(slug) {
  return CITY_PAGES.find(p => p.slug === slug);
}

export function getCityPageWithService(slug) {
  const page = getCityPage(slug);
  if (!page) return null;
  return { ...page, service: serviceBySlug(page.serviceSlug) };
}

export const RELATED_CITIES = (currentSlug) => {
  const current = getCityPage(currentSlug);
  if (!current) return [];
  return CITY_PAGES
    .filter(p => p.slug !== currentSlug)
    .map(p => ({
      slug: p.slug,
      title: p.title,
      city: p.city,
      sameService: p.serviceSlug === current.serviceSlug,
      sameCity: p.city === current.city,
    }))
    .sort((a, b) => (b.sameService - a.sameService) || (b.sameCity - a.sameCity))
    .slice(0, 6);
};
